"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";

/**
 * Screenshot grid with a lightbox. Keyboard: Escape closes, arrows step through.
 */
export default function ProjectGallery({
  images,
  title,
}: {
  images: string[];
  title: string;
}) {
  const t = useTranslations("Works");
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);

  const close = useCallback(() => setOpenIndex(null), []);
  const step = useCallback(
    (delta: number) =>
      setOpenIndex((index) =>
        index === null
          ? null
          : (index + delta + images.length) % images.length,
      ),
    [images.length],
  );

  useEffect(() => {
    if (openIndex === null) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      else if (event.key === "ArrowRight") step(1);
      else if (event.key === "ArrowLeft") step(-1);
    };

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [openIndex, close, step]);

  if (!images.length) return null;

  return (
    <>
      <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {images.map((src, index) => (
          <button
            key={src}
            type="button"
            onClick={() => setOpenIndex(index)}
            aria-label={t("galleryOpen", { number: index + 1 })}
            className="group relative aspect-[3/4] w-full overflow-hidden rounded-2xl border border-sky-500/25 bg-[#0b1220] transition-colors duration-300 hover:border-sky-500/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0CA5F0]"
          >
            <Image
              src={src}
              alt={t("galleryImageAlt", { title, number: index + 1 })}
              fill
              sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 22vw"
              className="object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </button>
        ))}
      </div>

      {openIndex !== null ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("galleryImageAlt", { title, number: openIndex + 1 })}
          onClick={close}
          className="fixed inset-0 z-100 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
        >
          <button
            type="button"
            onClick={close}
            aria-label={t("galleryClose")}
            className="absolute end-4 top-4 z-10 flex size-11 items-center justify-center rounded-full bg-black/60 border border-white/30 text-white transition-colors hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0CA5F0]"
          >
            <svg viewBox="0 0 24 24" className="size-6" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          {images.length > 1 ? (
            <>
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); step(-1); }}
                aria-label={t("galleryPrevious")}
                className="absolute start-2 top-1/2 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 sm:start-4 border border-white/30 text-white transition-colors hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0CA5F0]"
              >
                <svg viewBox="0 0 24 24" className="size-6 rtl:rotate-180" fill="none" aria-hidden="true">
                  <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); step(1); }}
                aria-label={t("galleryNext")}
                className="absolute end-2 top-1/2 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 sm:end-4 border border-white/30 text-white transition-colors hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0CA5F0]"
              >
                <svg viewBox="0 0 24 24" className="size-6 rtl:rotate-180" fill="none" aria-hidden="true">
                  <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </>
          ) : null}

          <div
            onClick={(event) => event.stopPropagation()}
            onTouchStart={(event) => {
              touchStartX.current = event.touches[0]?.clientX ?? null;
            }}
            onTouchEnd={(event) => {
              const start = touchStartX.current;
              touchStartX.current = null;
              if (start === null) return;

              const delta = (event.changedTouches[0]?.clientX ?? start) - start;
              if (Math.abs(delta) < 50) return;
              // Swiping right reveals the previous image in a left-to-right
              // gallery and the next one when the page reads right-to-left.
              const rtl = document.documentElement.dir === "rtl";
              step(delta > 0 === rtl ? 1 : -1);
            }}
            className="relative h-[72vh] w-full max-w-5xl touch-pan-y sm:h-[80vh]"
          >
            <Image
              src={images[openIndex]}
              alt={t("galleryImageAlt", { title, number: openIndex + 1 })}
              fill
              sizes="100vw"
              className="object-contain"
              priority
            />
          </div>

          <p className="absolute bottom-6 text-[14px] text-white/70">
            {openIndex + 1} / {images.length}
          </p>
        </div>
      ) : null}
    </>
  );
}
